/**
 * The cmx-document-content slot's shared identity and addressing rules.
 *
 * Split out of `CmxDocumentContentViewer.tsx` so state that has nothing to do with rendering --
 * the note key `NoteNav`/`NoteViewer` key off, and which id a document resolves to -- does not
 * require importing a component module. `noteInteractionContext.tsx` in particular is shared UI
 * state, not a component; it should not need to import a component to compute its initial value.
 */

/**
 * One cmx-document-content note, once something supplies one.
 *
 * Deliberately not in `project_starburst/interface/` -- the same place `NotesData` lives
 * (`stateMappers/caseRunToCodingScreenState.ts`), because a payload shape the adapter owns is
 * adapter-side knowledge.
 *
 * `documentId` is populated by `cmxDocumentContentFromCaseRun` and doubles as this entry's
 * `activeNoteKey`/`sourceModalNoteKey` -- legacy note keys are `String(note.document_id)`
 * (numeric), and cmx-document-content ids are GUIDs, so the two key spaces cannot collide and no
 * separate sentinel is needed. `layer` is NOT populated by the mapper, and must not be: supplying
 * it suppresses the status read in `CmxDocumentContentViewer` and pins the case to one layer.
 * `renditions` are the case's `DocumentMetadata` rows matched to this note by `documentId` --
 * empty/absent means no converted rendition has landed (or none matched), which is what hides the
 * original-document button (see `NoteNav`'s `hasRendition`).
 */
export type CmxDocumentContentSlotEntry = {
  readonly documentId: string;
  readonly layer?: number;
  readonly renditions?: readonly CMxAPI.DocumentMetadata[];
};

/**
 * The cmx-document-content slot's payload -- one entry per note carrying a `cmx_document_content_key`.
 * Plural because legacy notes are already plural and positional `Note #N` numbering is meaningless
 * for a single slot.
 */
export type CmxDocumentContentSlotData = readonly CmxDocumentContentSlotEntry[];

/**
 * Query parameters -- the override, not the primary path.
 *
 * The case supplies the id via `cmxDocumentContentFromCaseRun`, which reads a note-shaped
 * `caseRuns[].notes[]` entry carrying `cmx_document_content_key`. That shape is PROVISIONAL and only
 * `cmx-automate-stub` produces it today: `details/v2` is served by case-builder, whose `Note` DTO
 * has no such field, so against a real backend the bound value is absent and these parameters are
 * how a document is addressed. The bound value takes precedence when present.
 */
export const DOCUMENT_ID_PARAM = 'cmxDocumentContentId';
export const LAYER_PARAM = 'cmxDocumentContentLayer';

/**
 * `CmxDocumentContentViewer`/`NoteViewer` bind these helpers to a SINGLE selected entry (the one
 * matching the active note key), never the list itself.
 */
function readSlotData(value: unknown): Partial<CmxDocumentContentSlotEntry> {
  // Same defensive guard as NoteViewer's asNotesData: a non-object value must degrade to an
  // empty state, never throw and take the pane down with it.
  return value != null && typeof value === 'object'
    ? (value as Partial<CmxDocumentContentSlotEntry>)
    : {};
}

function readSearchParam(name: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

export function resolveDocumentId(value: unknown): string | null {
  return readSlotData(value).documentId ?? readSearchParam(DOCUMENT_ID_PARAM);
}

/**
 * The layer a caller EXPLICITLY asked for, or `null` to mean "pick the best one".
 *
 * `null` matters on its own: it is what tells `CmxDocumentContentViewer` to read status and pick the
 * highest ready layer, rather than pinning to a fixed default that could 404 against a layer the
 * service has not produced yet.
 */
export function resolveRequestedLayer(value: unknown): number | null {
  const bound = readSlotData(value).layer;
  if (typeof bound === 'number') {
    return bound;
  }
  const fromUrl = Number(readSearchParam(LAYER_PARAM));
  return Number.isInteger(fromUrl) && fromUrl > 0 ? fromUrl : null;
}
